import { useState, useMemo, useEffect } from 'react';
import { Plus, Filter, Info, AlertTriangle, ChevronsUpDown, FlaskConical } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useLocations } from '@/features/locations/hooks/useLocations';
import { useLineItemExclusions } from '../hooks/useLineItemExclusions';
import { ExclusionRulesTable } from './ExclusionRulesTable';
import { AddRuleModal } from './AddRuleModal';
import { TestOrderModal } from './TestOrderModal';

/**
 * Line-item exclusions UI.
 *
 * Rendered inside a Production Settings → General accordion section, so
 * this component renders flat content (no outer Cards) to match the
 * sibling sections' "embedded" convention. The accordion provides the
 * outer chrome.
 */
export default function LineItemExclusionsSettings() {
  const { t } = useTranslation('settings');
  const { locations, isLoading: locationsLoading } = useLocations();

  // '' sentinel for "no location chosen" keeps Radix Select controlled
  // from first render.
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [addOpen, setAddOpen] = useState(false);
  const [addScope, setAddScope] = useState<'org' | 'location'>('org');
  const [justAddedIds, setJustAddedIds] = useState<string[]>([]);
  const [locationOverrideOpen, setLocationOverrideOpen] = useState(false);

  // BLU-374: test-order modal — opened from either section's header.
  const [testOrderModalOpen, setTestOrderModalOpen] = useState(false);
  const [testOrderLocationDefault, setTestOrderLocationDefault] = useState<string | null>(null);

  const orgHook = useLineItemExclusions(null);
  const locHook = useLineItemExclusions(selectedLocationId || null);

  const activeLocations = useMemo(
    () => locations.filter((loc) => loc.is_active !== false),
    [locations],
  );

  // Clear the "just added" highlight after ~1.2s. Accumulate across
  // multiple rapid adds so each batch gets its flash instead of the
  // latest overwriting the previous set.
  useEffect(() => {
    if (justAddedIds.length === 0) return;
    const t = setTimeout(() => setJustAddedIds([]), 1200);
    return () => clearTimeout(t);
  }, [justAddedIds]);

  const openAdd = (scope: 'org' | 'location') => {
    setAddScope(scope);
    setAddOpen(true);
  };

  const orgDefaultsCount = orgHook.orgRules.length;
  const overrideSummaries = orgHook.overrideSummaries;
  const locationsWithOverridesCount = overrideSummaries.length;
  const showReplaceWarning =
    !!selectedLocationId &&
    locHook.inheritMode === 'replace' &&
    orgDefaultsCount > 0;

  // When the user opens "Configure" and there's at least one location with
  // overrides, jump straight to the first one so they can see the rules
  // immediately instead of having to pick a location first.
  const handleConfigureToggle = (next: boolean) => {
    setLocationOverrideOpen(next);
    if (next && !selectedLocationId && overrideSummaries[0]) {
      setSelectedLocationId(overrideSummaries[0].location_id);
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6">
        <Alert>
          <Filter className="h-4 w-4" />
          <AlertDescription>{t('exclusions.intro')}</AlertDescription>
        </Alert>

        {/* Org-wide defaults — flat section, no Card (accordion provides chrome) */}
        <section className="space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="text-sm font-semibold">{t('exclusions.orgDefaults.title')}</h4>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t('exclusions.orgDefaults.description')}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setTestOrderLocationDefault(null);
                  setTestOrderModalOpen(true);
                }}
              >
                <FlaskConical className="mr-1 h-4 w-4" />
                {t('exclusions.actions.testOrder', { defaultValue: 'Test order' })}
              </Button>
              <Button size="sm" onClick={() => openAdd('org')}>
                <Plus className="mr-1 h-4 w-4" />
                {t('exclusions.actions.addRule')}
              </Button>
            </div>
          </div>
          <ExclusionRulesTable
            rules={orgHook.orgRules}
            isLoading={orgHook.orgRulesLoading}
            locationId={null}
            emptyState={{
              title: t('exclusions.orgDefaults.emptyTitle'),
              description: t('exclusions.orgDefaults.emptyDescription'),
            }}
            highlightIds={addScope === 'org' ? justAddedIds : undefined}
          />
        </section>

        {/* Location overrides — always reachable, but collapsed by default so
            first-run users aren't overwhelmed. The trigger lives OUTSIDE the
            CollapsibleContent so even a brand-new admin with zero org rules
            can open it. */}
        <Separator />
        <Collapsible
          open={locationOverrideOpen}
          onOpenChange={handleConfigureToggle}
          className="space-y-4"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="text-sm font-semibold">
                {t('exclusions.locationOverride.title')}
              </h4>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t('exclusions.locationOverride.description')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm">
                  <ChevronsUpDown className="mr-1 h-4 w-4" />
                  {locationOverrideOpen
                    ? t('exclusions.locationOverride.hide')
                    : t('exclusions.locationOverride.configure')}
                </Button>
              </CollapsibleTrigger>
              {locationsWithOverridesCount > 0 ? (
                <Badge
                  variant="outline"
                  className="gap-1.5 border-emerald-500/40 bg-emerald-500/10 text-emerald-700 shadow-[0_0_0_3px_rgba(16,185,129,0.08)] dark:text-emerald-300"
                  aria-label={`${locationsWithOverridesCount} location${locationsWithOverridesCount === 1 ? '' : 's'} active`}
                >
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  </span>
                  {locationsWithOverridesCount} active
                </Badge>
              ) : null}
            </div>
          </div>
          <CollapsibleContent className="space-y-6">
                {/* Quick summary of every location that has overrides. Click
                    a chip to jump straight to that location's rules. */}
                {overrideSummaries.length > 0 ? (
                  <div className="rounded-md border bg-muted/40 p-3">
                    <p className="mb-2 text-xs font-semibold text-muted-foreground">
                      Active overrides ({overrideSummaries.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {overrideSummaries.map((s) => {
                        const isCurrent = s.location_id === selectedLocationId;
                        const ruleSummary =
                          s.rule_count === 0 && s.inherit_mode === 'replace'
                            ? 'Replace mode (no rules yet)'
                            : `${s.rule_count} rule${s.rule_count === 1 ? '' : 's'}` +
                              (s.inherit_mode === 'replace' ? ' • Replace' : '');
                        return (
                          <button
                            key={s.location_id}
                            type="button"
                            onClick={() => setSelectedLocationId(s.location_id)}
                            className={
                              'group flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors ' +
                              (isCurrent
                                ? 'border-primary bg-primary/10 text-foreground'
                                : 'border-border bg-background hover:bg-accent')
                            }
                          >
                            <span className="relative mt-1 flex h-1.5 w-1.5 shrink-0">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            </span>
                            <span className="flex flex-col gap-0.5">
                              <span className="font-semibold">
                                {s.location_name}
                              </span>
                              <span className="text-[10px] text-muted-foreground">
                                {ruleSummary}
                              </span>
                              {s.rules.length > 0 ? (
                                <span className="mt-0.5 line-clamp-1 max-w-[260px] text-[10px] text-muted-foreground">
                                  {s.rules
                                    .slice(0, 3)
                                    .map((r) => r.display_label ?? r.value)
                                    .join(' • ')}
                                  {s.rules.length > 3 ? ` • +${s.rules.length - 3}` : ''}
                                </span>
                              ) : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-col gap-2 sm:max-w-xs">
                  <Label htmlFor="exclusions-location">
                    {t('exclusions.locationOverride.locationLabel')}
                  </Label>
                  <Select
                    value={selectedLocationId}
                    onValueChange={(value) => setSelectedLocationId(value)}
                    disabled={locationsLoading}
                  >
                    <SelectTrigger id="exclusions-location">
                      <SelectValue
                        placeholder={t('exclusions.locationOverride.locationPlaceholder')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {activeLocations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedLocationId ? (
                  <>
                    <div className="rounded-md border p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-1.5">
                            <Label className="text-sm font-semibold">
                              {t('exclusions.inheritMode.label')}
                            </Label>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  aria-label={t('exclusions.inheritMode.tooltip')}
                                  className="rounded-sm text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                >
                                  <Info className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs text-xs">
                                {t('exclusions.inheritMode.tooltip')}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {t('exclusions.inheritMode.help')}
                          </p>
                        </div>
                      </div>
                      <RadioGroup
                        value={locHook.inheritMode}
                        onValueChange={(v) => locHook.setInheritMode(v as 'extend' | 'replace')}
                        disabled={locHook.inheritModeSaving}
                        className="mt-3 grid gap-2 sm:grid-cols-2"
                      >
                        <label
                          htmlFor="inherit-extend"
                          className="flex cursor-pointer gap-3 rounded-md border p-3 text-sm hover:bg-accent"
                        >
                          <RadioGroupItem value="extend" id="inherit-extend" className="mt-0.5" />
                          <div>
                            <div className="font-medium">
                              {t('exclusions.inheritMode.extend')}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {t('exclusions.inheritMode.extendDescription')}
                            </div>
                          </div>
                        </label>
                        <label
                          htmlFor="inherit-replace"
                          className="flex cursor-pointer gap-3 rounded-md border p-3 text-sm hover:bg-accent"
                        >
                          <RadioGroupItem value="replace" id="inherit-replace" className="mt-0.5" />
                          <div>
                            <div className="font-medium">
                              {t('exclusions.inheritMode.replace')}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {t('exclusions.inheritMode.replaceDescription')}
                            </div>
                          </div>
                        </label>
                      </RadioGroup>
                      {showReplaceWarning ? (
                        <Alert
                          variant="default"
                          className="mt-3 border-amber-500/50 bg-amber-500/10"
                        >
                          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
                          <AlertDescription className="text-amber-800 dark:text-amber-200">
                            {t('exclusions.inheritMode.replaceWarning', {
                              count: orgDefaultsCount,
                            })}
                          </AlertDescription>
                        </Alert>
                      ) : null}
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h5 className="text-sm font-semibold">
                          {t('exclusions.locationOverride.rulesHeading')}
                        </h5>
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setTestOrderLocationDefault(selectedLocationId);
                              setTestOrderModalOpen(true);
                            }}
                          >
                            <FlaskConical className="mr-1 h-4 w-4" />
                            {t('exclusions.actions.testOrder', {
                              defaultValue: 'Test order',
                            })}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openAdd('location')}>
                            <Plus className="mr-1 h-4 w-4" />
                            {t('exclusions.actions.addRuleForLocation')}
                          </Button>
                        </div>
                      </div>
                      <ExclusionRulesTable
                        rules={locHook.locationRules}
                        isLoading={locHook.locationRulesLoading}
                        locationId={selectedLocationId}
                        emptyState={{
                          title: t('exclusions.locationOverride.emptyTitle'),
                          description: t('exclusions.locationOverride.emptyDescription'),
                        }}
                        highlightIds={addScope === 'location' ? justAddedIds : undefined}
                      />
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('exclusions.locationOverride.prompt')}
                  </p>
                )}
          </CollapsibleContent>
        </Collapsible>

        <AddRuleModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          locationId={addScope === 'location' ? selectedLocationId || null : null}
          onCreated={(ids) =>
            setJustAddedIds((prev) => Array.from(new Set([...prev, ...ids])))
          }
        />

        <TestOrderModal
          open={testOrderModalOpen}
          onClose={() => setTestOrderModalOpen(false)}
          defaultLocationId={testOrderLocationDefault}
          locations={activeLocations}
        />
      </div>
    </TooltipProvider>
  );
}
